import { useEffect } from 'react';
import { useDispatchApp } from 'redux/rootSelectors';
import { checkedLocalization } from 'utils/checkedLocalization';
import { getReportsList, getReportSettings } from '../model/actions';
import {
  IUseReportBuilderEffectsProps,
  TGroupingItem,
  TGroupingsState,
  TObjectionTypes,
} from '../types';
import { useNextStepData, useReportSettings } from '../model/selectors';
import { getCurrentReportSettings } from '../utils';
import { setClearReport } from '../reducer';

export const useReportBuilderEffects = ({
  reportTypeId,
  reportsTypeList,
  formik,
  groupings,
  reports,
  setReportTypeId,
  setGroupings,
  setFilterings,
  setObjections,
  setStatistics,
  setColumns,
  resetForm,
}: IUseReportBuilderEffectsProps): void => {
  const dispatch = useDispatchApp();

  const { data: reportSettings } = useReportSettings();
  const { data: nextStepData } = useNextStepData();

  useEffect(() => {
    dispatch(getReportsList());
  }, []);

  useEffect(() => {
    if (reportTypeId) {
      dispatch(getReportSettings({ id: reportTypeId }));
    }
  }, [reportTypeId]);

  useEffect(() => {
    if (reportsTypeList && reportsTypeList.length > 0) {
      formik.setFieldValue('reportType', {
        label: checkedLocalization(
          `reportbuilder.types.${reportsTypeList[0].title}`,
          reportsTypeList[0].title,
          false,
        ),
        value: reportsTypeList[0].title,
      });
      setReportTypeId(reportsTypeList[0].id);
    }
  }, [reportsTypeList]);

  useEffect(() => {
    if (reportSettings?.grouping) {
      const newGroupings = getCurrentReportSettings(reportSettings.grouping);
      setGroupings(newGroupings);
    }
  }, [reportSettings?.grouping]);

  useEffect(() => {
    if (reportSettings?.filtering) {
      const newGroupings = getCurrentReportSettings(reportSettings.filtering);
      setFilterings(newGroupings);
    }
  }, [reportSettings?.filtering]);

  useEffect(() => {
    if (reportSettings?.statisticField) {
      const formatedStatisticField = reportSettings.statisticField.map(
        (statistic) => {
          const label = checkedLocalization(
            `reportbuilder.statistics.${statistic.title.toLocaleLowerCase()}`,
            statistic.title.toLocaleLowerCase(),
          );
          return { label, value: statistic.key };
        },
      );
      setStatistics(formatedStatisticField);
    }
  }, [reportSettings?.statisticField]);

  useEffect(() => {
    if (reportSettings?.objection) {
      const newGroupings: Record<TObjectionTypes, TGroupingItem> = {} as Record<
        TObjectionTypes,
        TGroupingItem
      >;

      Object.entries(reportSettings.objection).forEach(([key, value]) => {
        const typedKey = key as TObjectionTypes;
        newGroupings[typedKey] = {
          type: 'objection',
          label: typedKey,
          data: value,
        };
      });

      setObjections(newGroupings);
    }
  }, [reportSettings?.objection]);

  useEffect(() => {
    if (nextStepData) {
      const newGroupings: TGroupingsState = {};

      Object.entries(nextStepData).forEach(([key, value]) => {
        const typedKey = key;
        newGroupings[typedKey] = {
          type: 'objection',
          label: typedKey,
          data: value,
        };
      });

      setObjections((prev) => ({ ...prev, ...newGroupings }));
    }
  }, [nextStepData]);

  useEffect(() => {
    Object.entries(groupings).forEach(([key, value]) => {
      if (
        value.sql_key === 'date' &&
        Array.isArray(value.data) &&
        value.data.length > 1
      ) {
        formik.setFieldValue(`reportFilters.groupBy.${key}`, value.data[1]);
      }
    });
  }, [groupings]);


  useEffect(() => {
    if (!reports?.length) {
      setColumns([]);
      return;
    }

    const { groupBy: selectedGroupingValue } = formik.values.reportFilters;

    /** Настройки таблицы
     * Перевод столбцов, размер колонок, поиск колонок группировки
     */
    const WIDTH_COLUMN = 150;
    const MIN_LENGTH_TITLE = 15;
    const isSmallReport = reports.length <= 500;

    const groupingBySqlKey = new Map(
      Object.values(groupings).map((grouping) => [
        grouping.sql_key,
        grouping,
      ])
    );

    const columns = Object.keys(reports[0]).map((key) => {
      const currentGroupingFiled = groupingBySqlKey.get(key);

      const selectedCatalogData = currentGroupingFiled
        ? selectedGroupingValue[currentGroupingFiled.label]
        : null;

      const isCatalog = selectedCatalogData &&
        typeof selectedCatalogData === 'object' &&
        'label' in selectedCatalogData;

      const groupingLabel = isCatalog
        ? selectedCatalogData.label
        : currentGroupingFiled?.label;

      const translateTitle = checkedLocalization(
        `reportbuilder.groupings.${groupingLabel}`,
        key,
        false
      );

      const isLongKey = translateTitle.length > MIN_LENGTH_TITLE;

      const width = isLongKey
        ? WIDTH_COLUMN + (translateTitle.length - MIN_LENGTH_TITLE) * 10
        : WIDTH_COLUMN;

      const resultTitle = reportSettings?.statisticField?.find(
        (statistic) => statistic.key === key,
      )
        ? reportSettings?.statisticField?.find(
            (statistic) => statistic.key === key,
          )?.title
        : translateTitle;

      return {
        id: key,
        title: resultTitle,
        minWidth: 100,
        width,
        grow: (isCatalog && isSmallReport) || isLongKey,
      };
    });

    setColumns(columns);
  }, [
    reports,
    groupings,
  ]);

  useEffect(
    () => () => {
      dispatch(setClearReport());
      resetForm();
    },
    [],
  );
};
